import { api, mBrandResearchBaseURL } from "@/config/api";
import {
  InsightsRunResponseSchema,
  RUN_NO_SKILL_HINT,
  type InsightsRunRequest,
  type InsightsRunResponse,
} from "@/schema/brand-os/insights-run";

/**
 * Insights & Recommendations runner.
 *
 * Backend contract: POST /api/v1/ads-intelligence/insights/run
 *
 * Same structured payload for every successful run (`critical`, `opportunities`,
 * `wins`); `skill_id` is for labels/copy only. No FE-side skill picker.
 */

const ENDPOINT = mBrandResearchBaseURL + "/api/v1/ads-intelligence/insights/run";

/**
 * Recoverable variants the UI surfaces with bespoke copy / CTAs:
 *
 * - `NO_SKILL_LOADED` — agent answered but didn't pick an output skill.
 *   The run can be retried verbatim, but the user will get a better
 *   result by clarifying intent (diagnose vs. act).
 * - `AGENT_FAILED` — generic agent failure. Pure retry.
 * - `UNKNOWN` — non-503 / unparseable error. Pure retry, surface as toast.
 */
export type InsightsRunErrorCode = "NO_SKILL_LOADED" | "AGENT_FAILED" | "UNKNOWN";

export class InsightsRunApiError extends Error {
  readonly status: number;
  readonly code: InsightsRunErrorCode;
  readonly detail?: string;
  constructor(opts: {
    status: number;
    code: InsightsRunErrorCode;
    message: string;
    detail?: string;
  }) {
    super(opts.message);
    this.name = "InsightsRunApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.detail = opts.detail;
  }
}

async function parseError(response: Response): Promise<InsightsRunApiError> {
  const raw = (await response.json().catch(() => ({}))) as {
    detail?: unknown;
    error?: string;
    message?: string;
  };
  // FastAPI ships its 503 messages on `detail` (string). We sniff for the
  // "did not load an output skill" substring rather than a structured
  // error_code because the backend doesn't ship one for this surface.
  const detail =
    typeof raw.detail === "string"
      ? raw.detail
      : Array.isArray(raw.detail)
        ? raw.detail.map((d) => (typeof d === "string" ? d : JSON.stringify(d))).join("; ")
        : undefined;
  const message = detail || raw.error || raw.message || `Request failed (${response.status})`;

  if (response.status === 503) {
    if (detail?.includes(RUN_NO_SKILL_HINT)) {
      return new InsightsRunApiError({
        status: 503,
        code: "NO_SKILL_LOADED",
        message,
        detail,
      });
    }
    return new InsightsRunApiError({
      status: 503,
      code: "AGENT_FAILED",
      message,
      detail,
    });
  }
  return new InsightsRunApiError({
    status: response.status,
    code: "UNKNOWN",
    message,
    detail,
  });
}

function toRequestBody(req: InsightsRunRequest): Record<string, unknown> {
  // Drop nullish/empty fields so the backend's defaults kick in. Empty
  // string for `prompt` is intentionally left as-is — the backend will
  // reject it, and we want that signal to surface (vs. silently sending
  // a degenerate request).
  const out: Record<string, unknown> = { prompt: req.prompt };
  if (req.user_id) out.user_id = req.user_id;
  if (req.organization_id) out.organization_id = req.organization_id;
  if (req.customer_id) out.customer_id = req.customer_id;
  if (req.google_customer_id) out.google_customer_id = req.google_customer_id;
  if (req.currency) out.currency = req.currency;
  if (req.today) out.today = req.today;
  if (req.deep_research) out.deep_research = true;
  if (req.refresh) out.refresh = true;
  return out;
}

export const InsightsRunApiFactory = {
  Endpoint: ENDPOINT,
  Apis: {
    Run: async (req: InsightsRunRequest): Promise<InsightsRunResponse> => {
      const response = await api(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toRequestBody(req)),
      });
      if (!response.ok) throw await parseError(response);
      return response.json().then(InsightsRunResponseSchema.parse);
    },
  },
};
